import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon, TransferOutIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { isEmpty } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { SegmentedChecksFilter } from 'dashboard/components/ChecksFilter';
import CreateInquiryWizard from 'dashboard/components/CreateInquiry/CreateInquiryWizard';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import Permissioned from 'dashboard/components/Permissioned';
import {
  FilterTagsCondition,
  SegmentedTagsConditionFilter,
} from 'dashboard/components/TagsConditionFilter';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import { optionWithSubtextMapper } from 'dashboard/components-lib/MultiSelect/MultiSelect';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { INQUIRY_STATUS_LABEL_MAP } from 'dashboard/lib/constants/inquiry';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { AccountTypesFilterOption } from 'dashboard/models/account-type';
import { Environment, EnvironmentStatus } from 'dashboard/models/environment';
import { ExporterFileType } from 'dashboard/models/exporter';
import { Inquiry, InquiryStatus } from 'dashboard/models/inquiry';
import {
  InquiryTemplateFilterOption,
  InquiryTemplateStatus,
} from 'dashboard/models/inquiry-template';
import { List, ListStatus } from 'dashboard/models/list';
import { Organization } from 'dashboard/models/organization';
import { Tag } from 'dashboard/models/tag';
import { TemplateFilterOption } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import { User } from 'dashboard/models/user';
import {
  CheckFilterValues,
  InquiriesFilterParams,
  saveInquiries,
} from 'dashboard/redux/actions/inquiries';
import InquiriesTable from 'dashboard/views/DashboardInquiriesView/InquiriesTable';
import InquirySimulateBanner from 'dashboard/views/DashboardInquiriesView/InquirySimulateBanner';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel, PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isCreateEnabledTemplateBasic } from 'lib/utils/template';

const ButtonGroup = styled('div')`
  display: flex;
  grid-column-start: 4;
  justify-content: flex-end;

  > * + * {
    margin-left: ${Spacing[3]};
  }
`;

// this is the pagination limit, we should display over 10000 in modal once count is >= 10000
export const WARN_LARGE_EXPORT_CUTOFF = 10_000;
/**
 * @see Exporter::Inquiry::MAX_RECORD_COUNT
 */
export const MAX_EXPORT_COUNT_LIMIT = 100_000;

const STATUS_FILTER_OPTIONS = Object.values(InquiryStatus).map((value) => ({
  label: INQUIRY_STATUS_LABEL_MAP[value],
  value,
}));

export interface DashboardInquiriesViewProps {
  inquiries: Inquiry[];
  templateFilterOptions: (TemplateFilterOption | InquiryTemplateFilterOption)[];
  accountTypeBasics: AccountTypesFilterOption[];
  themes: Theme[];
  lists: List[];
  tags: Tag[];
  pageSize: number;
  inquiryFilters: InquiriesFilterParams;
  inquiryPaginationParams: PaginationParams;
  isLoadingInquiries: boolean;
  isLoadingTags: boolean;
  sortParams: SortParams;
  currentEnvironment: Environment;
  organization: Organization;
  exportersProductFeatureIsDisabled: boolean;
  currentUser: User;
  handleExportData: (exporterFileType: ExporterFileType) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
  handleChecksFilterUpdate: (checks: CheckFilterValues[]) => void;
  handleStatusFilterUpdate: (inquiryStatuses: InquiryStatus[]) => void;
  handleTemplateFilterUpdate: (templateIds: string[]) => void;
  handleTagFilterUpdate: (tagIds: string[]) => void;
  handleTagsConditionsFilterUpdate: (tagsConditions: FilterTagsCondition[][]) => void;
  handleListFilterUpdate: (listIds: string[]) => void;
  handleReusablePersonaUsedFilterUpdate: (reusablePersonaUsed: string) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
}

const DashboardInquiriesView = ({
  inquiries = [],
  templateFilterOptions,
  accountTypeBasics,
  themes,
  lists,
  tags,
  inquiryFilters,
  inquiryPaginationParams,
  pageSize,
  isLoadingInquiries,
  sortParams,
  currentEnvironment,
  organization,
  exportersProductFeatureIsDisabled,
  currentUser,
  handleExportData,
  handlePageSizeClick,
  handlePageLinkClick,
  handleDateUpdate,
  handleChecksFilterUpdate,
  handleStatusFilterUpdate,
  handleTemplateFilterUpdate,
  handleTagsConditionsFilterUpdate,
  handleListFilterUpdate,
  handleReusablePersonaUsedFilterUpdate,
  handleOmniSearch,
  handleSortUpdate,
}: DashboardInquiriesViewProps) => {
  const {
    checks,
    createdAtStart,
    createdAtEnd,
    status,
    templateIds,
    tagsConditions,
    listIds,
    omni,
    reusablePersonaUsed,
  } = inquiryFilters;

  const dispatch = useDispatch();
  // The inquiries index endpoint is eventually consistent. Therefore, we cannot refresh the data in the
  // InquiriesTable using this endpoint because at the time of calling this endpoint, a newly created inquiry
  // may not yet be returned. Instead, simply append the created inquiry to the list of inquiries in redux state
  const addCreatedInquiryToList = useCallback(
    (createdInquiry: Inquiry) => {
      dispatch(
        saveInquiries([createdInquiry, ...inquiries], currentEnvironment, inquiryPaginationParams),
      );
    },
    [dispatch, inquiries, currentEnvironment, inquiryPaginationParams],
  );

  const [
    showCreateInquiryWizard,
    { enable: openCreateInquiryWizard, disable: hideCreateInquiryWizard },
  ] = useToggle();

  const [showLargeExportModal, { enable: openLargeExportModal, disable: hideLargeExportModal }] =
    useToggle();

  const [
    showExportTooLargeModal,
    { enable: openExportTooLargeModal, disable: hideExportTooLargeModal },
  ] = useToggle();

  const totalInquiriesCount = inquiryPaginationParams?.['total-count'] ?? 0;

  const statusFilterLabelTextMapper = useLabelTextMapper(STATUS_FILTER_OPTIONS, 'statuses', {
    emptyIsAll: true,
  });

  const displayTemplateFilterOptions = useMemo(
    () =>
      templateFilterOptions
        .filter(
          (option) =>
            option.type === ApiModel.Template || option.status === InquiryTemplateStatus.Active,
        )
        .map(({ name, id }) => ({
          label: `${name} (${id})`,
          customLabel: optionWithSubtextMapper(name, id),
          value: id,
        })),
    [templateFilterOptions],
  );

  const templateFilterLabelTextMapper = useLabelTextMapper(
    displayTemplateFilterOptions,
    'inquiry templates',
    {
      emptyIsAll: true,
    },
  );

  const tagIdFilterOptions = tags.map((tag) => {
    return { label: tag.attributes.name, value: tag.id };
  });

  const listFilterOptions = useMemo(
    () =>
      (lists || [])
        .filter((list) => list.attributes.status === ListStatus.Active)
        .map((list) => ({
          label: list.attributes.name,
          value: list.id,
        })),
    [lists],
  );

  const listFilterLabelTextMapper = useCallback(
    (values: string[]) => {
      if (values.length === 0 || values.length === listFilterOptions.length) {
        return 'All lists';
      } else if (values.length === 1) {
        return listFilterOptions.find((v) => v.value === values[0])?.label;
      } else {
        return `Matching ${values.length} Lists`;
      }
    },
    [listFilterOptions],
  );

  const [exporterFileType, setExporterFileType] = useState(ExporterFileType.TarGz);

  const createEnabledTemplateBasics = templateFilterOptions.filter(isCreateEnabledTemplateBasic);
  const initialTemplateBasic = createEnabledTemplateBasics[0];

  const isCurrentEnvironmentActive =
    currentEnvironment.attributes.status === EnvironmentStatus.Active;

  const disableCreateInquiry =
    !isCurrentEnvironmentActive || createEnabledTemplateBasics.length === 0;

  const location = useLocation();
  const hasInquiryCreatePermission = usePermission(Permission.InquiryCreate);
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openCreateInquiryModalParam = queryParams.get('openCreateInquiryModal');

    if (
      openCreateInquiryModalParam === 'true' &&
      hasInquiryCreatePermission &&
      !disableCreateInquiry
    ) {
      openCreateInquiryWizard();
    }
  }, [location, hasInquiryCreatePermission, disableCreateInquiry, openCreateInquiryWizard]);

  const showReusablePersonaUsedFilter = useFeatureFlag(
    'dashboard:show-reusable-persona-used-filter',
  );

  const onHandleExportClick = (fileType: ExporterFileType) => {
    setExporterFileType(fileType);
    if (totalInquiriesCount >= WARN_LARGE_EXPORT_CUTOFF) {
      if (totalInquiriesCount < MAX_EXPORT_COUNT_LIMIT) {
        openLargeExportModal();
      } else {
        openExportTooLargeModal();
      }
    } else {
      handleExportData(fileType);
    }
  };

  // By default, we want to always use the legacy account type.
  const initialAccountType =
    accountTypeBasics.find((accountTypeBasic) => accountTypeBasic['is-legacy-account-type']) ??
    accountTypeBasics[0];

  const filterConfig: FilterConfig[] = [
    {
      id: 'dateCreated',
      name: 'Date created',
      type: 'daterange',
      onChange: handleDateUpdate,
      value: {
        startDate: createdAtStart,
        endDate: createdAtEnd,
      },
    },
    {
      id: 'verificationChecks',
      htmlId: 'inquiries-view__checks',
      name: 'Verification checks',
      type: 'custom',
      filterElement: (
        <SegmentedChecksFilter
          onChange={handleChecksFilterUpdate}
          dataTest='verifications__select--checks'
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value={checks}
          size='md'
        />
      ),
      onDelete: () => handleChecksFilterUpdate([]),
      hasValue: checks && checks.length > 0,
    },
    {
      id: 'status',
      htmlId: 'inquiries-view__status',
      name: 'Status',
      type: 'multiselect-legacy',
      selectOptionValues: STATUS_FILTER_OPTIONS,
      labelTextMapper: statusFilterLabelTextMapper,
      onChange: handleStatusFilterUpdate,
      value: status || [],
    },
    {
      id: 'reusablePersonaUsed',
      htmlId: 'inquiries-view__reusable-persona-used',
      name: 'Reusable persona used',
      type: 'select',
      options: useMemo(
        () => [
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' },
        ],
        [],
      ),
      onChange: handleReusablePersonaUsedFilterUpdate,
      value: reusablePersonaUsed ?? '',
      enabled: showReusablePersonaUsedFilter,
    },
    {
      id: 'inquiryTemplates',
      htmlId: 'inquiries-view__template-filter-options',
      name: 'Inquiry templates',
      type: 'multiselect-legacy',
      enabled: templateFilterOptions.length > 1,
      searchEnabled: true,
      selectOptionValues: displayTemplateFilterOptions,
      labelTextMapper: templateFilterLabelTextMapper,
      onChange: handleTemplateFilterUpdate,
      value: templateIds || [],
      minWidth: '250px',
    },
    {
      id: 'listMatches',
      htmlId: 'inquiries-view__list-matches',
      name: 'List matches',
      type: 'multiselect-legacy',
      enabled: listFilterOptions.length > 1,
      selectOptionValues: listFilterOptions,
      labelTextMapper: listFilterLabelTextMapper,
      onChange: handleListFilterUpdate,
      value: listIds || [],
    },
    {
      id: 'tag',
      htmlId: 'inquiries-view__tags-conditions',
      name: 'Tag',
      type: 'custom',
      enabled: tagIdFilterOptions.length >= 1,
      filterElement: (
        <SegmentedTagsConditionFilter
          values={tagsConditions}
          tagOptions={tagIdFilterOptions}
          onChange={(tagsConditions: FilterTagsCondition[][]) => {
            handleTagsConditionsFilterUpdate(tagsConditions);
          }}
          productName='inquiry'
        />
      ),
      onDelete: () => handleTagsConditionsFilterUpdate([]),
      hasValue: tagsConditions && tagsConditions.length > 0,
    },
  ];

  return (
    <Container>
      <InactiveEnvironmentBanner />
      <Header title='Inquiries'>
        <ButtonGroup>
          <Permissioned permission={Permission.InquiryCreate}>
            <Button
              id='inquiries-view__create-inquiry-button'
              name='create-inquiry.button'
              data-test='inquiries-view__create-inquiry-button'
              onClick={openCreateInquiryWizard}
              prefixIcon={<PlusIcon />}
              disabled={disableCreateInquiry}
            >
              Create inquiry
            </Button>
          </Permissioned>
          {!exportersProductFeatureIsDisabled && (
            <Permissioned permission={Permission.InquiriesExport}>
              <DropdownMenu
                trigger={
                  <Button
                    id='inquiries-view__export'
                    data-test='inquiries-view__export'
                    variant='basic'
                    disabled={totalInquiriesCount === 0}
                    prefixIcon={<TransferOutIcon />}
                  >
                    Export
                  </Button>
                }
                disabled={totalInquiriesCount === 0}
                align='end'
              >
                {Object.values(ExporterFileType).map((fileType) => (
                  <DropdownMenu.Item
                    id={`inquiries-view__export__${fileType}`}
                    data-test={`inquiries-view__export__${fileType}`}
                    onClick={() => {
                      onHandleExportClick(fileType);
                    }}
                    key={`inquiries-view__export__${fileType}`}
                  >
                    {`To ${fileType}`}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu>
            </Permissioned>
          )}
        </ButtonGroup>
      </Header>
      <Section>
        {showCreateInquiryWizard && (
          <CreateInquiryWizard
            templateFilterOptions={createEnabledTemplateBasics}
            account={null}
            accountTypeBasics={accountTypeBasics}
            organization={organization}
            currentEnvironment={currentEnvironment}
            themes={themes}
            onInquiryCreate={addCreatedInquiryToList}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            emailAddressPrefill={
              currentEnvironment.type === ApiModel.EnvironmentSandbox
                ? currentUser.attributes['email-address']
                : null
            }
            hideCreateInquiryWizard={hideCreateInquiryWizard}
            isOpen={showCreateInquiryWizard}
            initialValues={{
              templateId: initialTemplateBasic?.id,
              accountTypeId: initialAccountType?.id,
              themeId:
                initialTemplateBasic?.type === ApiModel.Template
                  ? initialTemplateBasic['default-theme-id']
                  : undefined,
            }}
          />
        )}
        <ModalWrapper isOpen={showLargeExportModal} handleCloseModal={hideLargeExportModal}>
          <ConfirmationModal
            cancelAction={hideLargeExportModal}
            confirmAction={() => {
              handleExportData(exporterFileType);
            }}
            header='Large export'
            confirmActionText='Export anyways'
            cancelActionText='Cancel'
          >
            <p>
              You are exporting <strong>over {WARN_LARGE_EXPORT_CUTOFF}</strong> inquiries. This may
              take a long time. We recommend limiting the amount of inquiries exported by setting
              filters.
            </p>
            <p>Exports that take longer than 3 hours will be canceled.</p>
          </ConfirmationModal>
        </ModalWrapper>

        <ModalWrapper isOpen={showExportTooLargeModal} handleCloseModal={hideExportTooLargeModal}>
          <ModalContainer data-test='inquiries-view__export-too-large-modal'>
            <ModalHeader onClose={hideExportTooLargeModal}>Export size limit exceeded</ModalHeader>

            <ModalBody>
              <Paragraph>
                You are attempting to export{' '}
                <strong>over {MAX_EXPORT_COUNT_LIMIT.toLocaleString()}</strong> inquiries. This is
                more than the allowable number of objects to export. Please change the filters and
                try again.
              </Paragraph>

              <Spacer height={4} />

              <Button
                width='button'
                autoFocus={true}
                id='confirmation-modal__cancel'
                data-test='confirmation-modal__cancel'
                variant='basic'
                onClick={hideExportTooLargeModal}
              >
                Revise filters
              </Button>
            </ModalBody>
          </ModalContainer>
        </ModalWrapper>

        {currentEnvironment.type === ApiModel.EnvironmentSandbox && <InquirySimulateBanner />}

        <Card>
          <Card.Body>
            <Card.Title>Inquiries</Card.Title>
            <Permissioned permission={Permission.InquiryList}>
              <TableControls
                searchProps={{
                  id: 'inquiries-table__search',
                  handleInput: handleOmniSearch,
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  value: omni,
                  placeholder: 'Search by inquiry ID, reference ID, or name',
                }}
                filters={filterConfig}
              />
            </Permissioned>
            <InquiriesTable
              dataTest='inquiries-view__inquiries-table'
              hasUsedFilters={
                ![checks, createdAtStart, createdAtEnd, status, templateIds, listIds, omni].every(
                  isEmpty,
                )
              }
              environment={currentEnvironment}
              inquiries={inquiries}
              canSort={isEmpty(omni)}
              sortParams={sortParams}
              loading={isLoadingInquiries}
              handleSortUpdate={handleSortUpdate}
            />
            <Pagination
              pageSize={pageSize}
              paginationParams={inquiryPaginationParams}
              handlePageSizeClick={handlePageSizeClick}
              handlePageLinkClick={handlePageLinkClick}
            />
          </Card.Body>
        </Card>
      </Section>
    </Container>
  );
};

export default DashboardInquiriesView;
